import {useState} from 'react';
import {useQuery} from '@apollo/client/react';
import {Table, Tag, Tooltip} from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';

import {Enum_Notification_State, Enum_Notification_Template, UserNotificationsQuery} from '../../../../__generated__/graphql';
import {QUERY_USER_NOTIFICATIONS} from '../../../../shared/graphql/admin/users';
import {currencyFormat} from '../../../../shared/helpers/currencyFormat';

type INotification = NonNullable<UserNotificationsQuery['notifications_connection']>['nodes'][number]
type IDebtAccount = {
    children?: string[]
    balance?: number
}

const PAGE_SIZE = 10

const templateNames: Record<Enum_Notification_Template, string> = {
    createClient: 'Создание учетной записи',
    updatePassword: 'Смена пароля',
    paymentDebt: 'Задолженность по балансу',
}

function stateDisplay(notification: INotification) {
    switch (notification.state) {
        case 'send': return <Tag color={'success'}>Отправлено</Tag>
        case 'error': return <Tooltip title={notification.errorMessage}><Tag color={'error'}>Ошибка</Tag></Tooltip>
        default: return <Tag color={'processing'}>Ожидает отправки</Tag>
    }
}

interface IUserNotifications {
    id: string
}

export const UserNotifications = ({id}: IUserNotifications) => {

    const [page, setPage] = useState<number>(1)

    const query = useQuery(QUERY_USER_NOTIFICATIONS, {
        variables: {
            userId: id,
            pagination: {page, pageSize: PAGE_SIZE},
        },
        fetchPolicy: 'no-cache',
    })

    const dataSource = _.map(query.data?.notifications_connection?.nodes, item => ({
        ...item,
        key: item.documentId,
    }))

    const columns = [
        {
            title: 'Дата',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (value: string | null) => value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—',
        },
        {
            title: 'Уведомление',
            dataIndex: 'template',
            key: 'template',
            render: (template: Enum_Notification_Template) => templateNames[template] || template,
        },
        {
            title: 'Баланс в письме',
            dataIndex: 'payload',
            key: 'payload',
            render: (_payload: unknown, record: INotification) => {
                const accounts = record.template === 'paymentDebt'
                    ? (record.payload as {accounts?: IDebtAccount[]} | null)?.accounts
                    : undefined
                return accounts?.length ?
                    _.map(accounts, (account, index) => (
                        <div key={index}>
                            {(account.children || []).join(', ')}: {currencyFormat(account.balance || 0)} ₽
                        </div>
                    ))
                    : '—'
            },
        },
        {
            title: 'Статус',
            dataIndex: 'state',
            key: 'state',
            render: (_state: Enum_Notification_State | null, record: INotification) => stateDisplay(record),
        },
    ];

    return (
        <Table
            dataSource={dataSource}
            columns={columns}
            loading={query.loading}
            pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total: query.data?.notifications_connection?.pageInfo.total,
                onChange: setPage,
                showSizeChanger: false,
            }}
        />
    )
}
